import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { scansApi, Scan, CreateScanRequest, UpdateScanRequest, GetScansParams } from '../services/scansApi'

interface ScansState {
  scans: Scan[]
  pagination: {
    total: number
    page: number
    limit: number
  }
  loading: boolean
  error: string | null
}

interface ScansActions {
  fetchScans: (params?: GetScansParams) => Promise<void>
  createScan: (scanData: CreateScanRequest) => Promise<void>
  updateScan: (scanId: string, scanData: UpdateScanRequest) => Promise<void>
  deleteScan: (scanId: string) => Promise<void>
  clearError: () => void
}

type ScansStore = ScansState & ScansActions

export const useScansStore = create<ScansStore>()(
  persist(
    (set) => ({
      scans: [],
      pagination: {
        total: 0,
        page: 1,
        limit: 10
      },
      loading: false,
      error: null,

      fetchScans: async (params?: GetScansParams) => {
        set({ loading: true, error: null })
        try {
          const response = await scansApi.getScans(params)
          if (response.success && response.data) {
            set({
              scans: response.data,
              pagination: {
                total: response.data.length, // This will be updated when we get server-side pagination info
                page: params?.page || 1,
                limit: params?.limit || 10
              },
              loading: false,
              error: null,
            })
          } else {
            set({
              scans: [],
              pagination: { total: 0, page: 1, limit: 10 },
              loading: false,
              error: 'Failed to fetch scans',
            })
          }
        } catch (error: any) {
          set({
            scans: [],
            pagination: { total: 0, page: 1, limit: 10 },
            loading: false,
            error: error.message || 'Failed to fetch scans',
          })
        }
      },

      createScan: async (scanData: CreateScanRequest) => {
        set({ loading: true, error: null })
        try {
          const response = await scansApi.createScan(scanData)
          if (response.success && response.data) {
            set(state => ({
              scans: [...state.scans, response.data!],
              loading: false,
              error: null,
            }))
          } else {
            set({
              loading: false,
              error: 'Failed to create scan',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to create scan',
          })
        }
      },

      updateScan: async (scanId: string, scanData: UpdateScanRequest) => {
        set({ loading: true, error: null })
        try {
          const response = await scansApi.updateScan(scanId, scanData)
          if (response.success && response.data) {
            const updatedScan = response.data
            set(state => ({
              scans: state.scans.map(scan =>
                scan._id === scanId ? updatedScan : scan
              ),
              loading: false,
              error: null,
            }))
          } else {
            set({
              loading: false,
              error: 'Failed to update scan',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to update scan',
          })
        }
      },

      deleteScan: async (scanId: string) => {
        set({ loading: true, error: null })
        try {
          const response = await scansApi.deleteScan(scanId)
          if (response.success) {
            set(state => ({
              scans: state.scans.filter(scan => scan._id !== scanId),
              loading: false,
              error: null,
            }))
          } else {
            set({
              loading: false,
              error: 'Failed to delete scan',
            })
          }
        } catch (error: any) {
          set({
            loading: false,
            error: error.message || 'Failed to delete scan',
          })
        }
      },

      clearError: () => set({ error: null }),
    }),
    {
      name: 'scans-storage',
    }
  )
)
